import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { API } from "../../../cache";

// Same react-markdown-without-rehype-raw convention CopilotPage.jsx already
// uses for chat replies — parses to React elements only, never injects raw
// HTML, so this stays safe even though the content below is static/trusted
// anyway. {{API}} is swapped for the real API base at render time (see
// MCP_GUIDE_MD below) rather than embedded as a JS template literal, so the
// backtick-fenced code blocks inside this string don't need any escaping.
//
// There is deliberately no key-generate/list/revoke UI on this page anymore
// — MCP access is per-user OAuth (app/routers/mcp_oauth.py, backend repo),
// delegating the actual login to this app's own Azure AD sign-in. There is
// nothing to generate or copy; the client (Claude, mcp-remote, etc.) prompts
// the user to sign in the first time it's used, exactly like signing into
// ProTrack itself.
const MCP_GUIDE_TEMPLATE = `
ProTrack runs an [MCP](https://modelcontextprotocol.io) server that lets an
MCP-aware AI assistant — Claude Desktop, Claude Code, ChatGPT (with MCP/
connector support), or any other MCP client — query live ProTrack data
directly in conversation: tasks, defects, epics, projects, teams, users, and
logged timesheet effort.

**This is read-only.** The assistant can search and summarize; it cannot
create, edit, or delete anything in ProTrack.

**Who can use it**: any Internal ProTrack account. Each person signs in with
their own Microsoft/Azure AD login — the assistant only ever sees what that
signed-in person could see in ProTrack themselves (their own visibility
rules, team scope, everything). There is no shared key or token to generate
or hand out.

### 1. Server details

| | |
|---|---|
| URL | \`{{API}}/mcp\` |
| Transport | MCP Streamable HTTP |
| Auth | OAuth 2.0 (sign in with your Microsoft/ProTrack account) |

### 2. Connect a client

**claude.ai → Settings → Connectors → Add custom connector**: paste the URL
above. Claude will detect this server supports sign-in and offer **"Sign in
now"** — click it, sign in with your Microsoft account the same way you log
into ProTrack, and you're connected. No token to copy anywhere.

**Claude Desktop / Claude Code**, via [\`mcp-remote\`](https://www.npmjs.com/package/mcp-remote)
(add this to \`claude_desktop_config.json\`):

\`\`\`json
{
  "mcpServers": {
    "protrack": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "{{API}}/mcp"]
    }
  }
}
\`\`\`

No headers, no token env var. The first time it connects, \`mcp-remote\`
opens your browser to sign in, then caches the result locally on your own
machine — restart the client after saving the config. Requires Node.js
installed locally (for \`npx\`).

### 3. Verify it's working

Ask your assistant something like:

> "Using the protrack tools, how many open defects are there right now?"

If it's connected, it should call a tool (e.g. \`defect_breakdown\`) and give
you a real number rather than saying it has no such capability.

### What the assistant can actually do

The same 14 read-only tools the in-app AI Copilot uses: search/breakdown
tasks, defects, epics, projects, users, teams, and logged timesheet effort,
plus reading a single task/defect/epic's full detail and its discussion
thread.

### Troubleshooting

- **Stuck on "Sign in" / access denied after signing in** — MCP access
  requires an Internal ProTrack account; External accounts can't sign in
  here, same as the rest of ProTrack.
- **Was working, now asks to sign in again** — access tokens expire after an
  hour and refresh automatically behind the scenes; if your ProTrack account
  was deactivated or your session was invalidated, you'll be asked to sign
  in again the same way the main app would ask you to.
- **Client can't reach the server at all** — confirm the URL is reachable
  from where the client runs, and that you're using a client/bridge that
  supports OAuth-authenticated Streamable HTTP (see \`mcp-remote\` above if
  your client only supports local stdio servers).
`;

export default function McpSettingsSection() {
  const guideMd = MCP_GUIDE_TEMPLATE.replaceAll("{{API}}", API);

  return (
    <>
      <p className="stp-section-title">Admin</p>
      <h2 className="stp-section-heading">MCP</h2>

      <div className="stp-card">
        <div className="stp-card-header">
          <div>
            <div className="stp-card-title">Connect an AI Assistant</div>
            <div className="stp-card-desc">How to point Claude Desktop, Claude Code, ChatGPT, or any other MCP client at ProTrack.</div>
          </div>
        </div>
        <div className="stp-card-body stp-mcp-guide">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{guideMd}</ReactMarkdown>
        </div>
      </div>
    </>
  );
}
